import React, { createContext, useContext, useState, useCallback } from 'react';
import { MOCK_COLIS } from './mockData';

// ─── Contexte ───────────────────────────────────────────────────────────────
export const AppContext = createContext(null);

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside ColisContext');
  return ctx;
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function generateId() {
  return 'COL-' + Date.now().toString().slice(-7);
}
function generateQR(id) {
  return `QR-${id}`;
}

function generateUserId() {
  return 'USR-' + Date.now().toString().slice(-7);
}

function safeJsonParse(raw, fallback) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

// ─── Provider ───────────────────────────────────────────────────────────────
export default function ColisContext({ children }) {
  const [colis, setColis]   = useState(MOCK_COLIS);
  const [toast, setToast]   = useState(null);
  const [user, setUserState] = useState(() => {
    const session = safeJsonParse(localStorage.getItem('zaki_session'), null);
    return session && session.id ? session : null;
  });
  const [myColisIds, setMyColisIdsState] = useState(() => {
    if (!user || !user.id) return [];
    const parsed = safeJsonParse(localStorage.getItem(`zaki_my_colis_ids_${user.id}`), []);
    return Array.isArray(parsed) ? parsed : [];
  });
  const [clientTel, setClientTelState] = useState(() => {
    try {
      return localStorage.getItem('zaki_client_tel') || '';
    } catch {
      return '';
    }
  });

  // Toast helper
  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const setClientTel = useCallback((tel) => {
    const next = (tel || '').trim();
    setClientTelState(next);
    try {
      if (!next) localStorage.removeItem('zaki_client_tel');
      else localStorage.setItem('zaki_client_tel', next);
    } catch {
      // ignore storage errors
    }
  }, []);

  const persistMyColisIds = useCallback((ids, nextUser = user) => {
    setMyColisIdsState(ids);
    if (!nextUser || !nextUser.id) return;
    try {
      localStorage.setItem(`zaki_my_colis_ids_${nextUser.id}`, JSON.stringify(ids));
    } catch {
      // ignore storage errors
    }
  }, [user]);

  const getUsers = useCallback(() => {
    const parsed = safeJsonParse(localStorage.getItem('zaki_users'), []);
    return Array.isArray(parsed) ? parsed : [];
  }, []);

  const setUsers = useCallback((users) => {
    localStorage.setItem('zaki_users', JSON.stringify(users));
  }, []);

  const setSession = useCallback((sessionUser) => {
    setUserState(sessionUser);
    localStorage.setItem('zaki_session', JSON.stringify(sessionUser));
    const ids = safeJsonParse(localStorage.getItem(`zaki_my_colis_ids_${sessionUser.id}`), []);
    setMyColisIdsState(Array.isArray(ids) ? ids : []);
  }, []);

  const register = useCallback((email, password, tel) => {
    const e = (email || '').trim().toLowerCase();
    const p = (password || '').trim();
    const t = (tel || '').trim();
    if (!e || !p || !t) throw new Error('Email, téléphone et mot de passe requis');

    const users = getUsers();
    if (users.some(u => (u.email || '').toLowerCase() === e)) {
      throw new Error('Cet email est déjà utilisé');
    }

    const newUser = { id: generateUserId(), email: e, tel: t, password: p };
    setUsers([newUser, ...users]);
    setSession({ id: newUser.id, email: newUser.email, tel: newUser.tel });
    showToast('Compte créé avec succès ✅', 'success');
    return newUser;
  }, [getUsers, setSession, setUsers, showToast]);

  const login = useCallback((email, password) => {
    const e = (email || '').trim().toLowerCase();
    const p = (password || '').trim();
    if (!e || !p) throw new Error('Email et mot de passe requis');
    const users = getUsers();
    const found = users.find(u => (u.email || '').toLowerCase() === e);
    if (!found || found.password !== p) throw new Error('Identifiants invalides');
    setSession({ id: found.id, email: found.email, tel: found.tel || '' });
    showToast('Connexion réussie ✅', 'success');
    return found;
  }, [getUsers, setSession, showToast]);

  const logout = useCallback(() => {
    setUserState(null);
    setMyColisIdsState([]);
    try {
      localStorage.removeItem('zaki_session');
    } catch {
      // ignore
    }
    showToast('Déconnecté', 'info');
  }, [showToast]);

  // Ajouter un nouveau colis
  const addColis = useCallback((formData) => {
    const id = generateId();
    const newColis = {
      ...formData,
      id,
      qr: generateQR(id),
      statut: 'depot',
      livreur: '',
      date: new Date().toISOString(),
    };
    setColis(prev => [newColis, ...prev]);
    if (user && user.id) persistMyColisIds([id, ...myColisIds]);
    showToast('Colis enregistré avec succès ! 📦', 'success');
    return newColis;
  }, [myColisIds, persistMyColisIds, showToast, user]);

  const clearMyColis = useCallback(() => {
    persistMyColisIds([]);
  }, [persistMyColisIds]);

  // Mettre à jour le statut d'un colis
  const updateStatut = useCallback((id, statut, extra = {}) => {
    setColis(prev =>
      prev.map(c => c.id === id ? { ...c, statut, ...extra } : c)
    );
  }, []);

  // Affecter un livreur
  const affecterLivreur = useCallback((id, livreur) => {
    updateStatut(id, 'affecte', { livreur });
    showToast(`Colis affecté à ${livreur} 🛵`, 'success');
  }, [updateStatut, showToast]);

  // Mettre en route
  const mettreEnRoute = useCallback((id) => {
    updateStatut(id, 'en_route');
    showToast('Colis en route 🚚', 'success');
  }, [updateStatut, showToast]);

  // Marquer livré
  const marquerLivre = useCallback((id) => {
    updateStatut(id, 'livre');
    showToast('Colis livré avec succès 🎉', 'success');
  }, [updateStatut, showToast]);

  // Marquer échec
  const marquerEchec = useCallback((id, motif = '') => {
    updateStatut(id, 'echec', { motif: (motif || '').trim() });
    showToast('Échec de livraison signalé ❌', 'error');
  }, [updateStatut, showToast]);

  // Supprimer
  const supprimerColis = useCallback((id) => {
    setColis(prev => prev.filter(c => c.id !== id));
    showToast('Colis supprimé', 'info');
  }, [showToast]);

  // Stats calculées
  const stats = {
    total:    colis.length,
    depot:    colis.filter(c => c.statut === 'depot').length,
    en_cours: colis.filter(c => ['affecte', 'en_route'].includes(c.statut)).length,
    livre:    colis.filter(c => c.statut === 'livre').length,
    echec:    colis.filter(c => c.statut === 'echec').length,
    ca:       colis
                .filter(c => c.statut === 'livre')
                .reduce((sum, c) => sum + (c.montant || 0), 0),
  };

  const value = {
    colis,
    stats,
    toast,
    myColisIds,
    user,
    clientTel,
    setClientTel,
    showToast,
    dismissToast,
    addColis,
    clearMyColis,
    register,
    login,
    logout,
    affecterLivreur,
    mettreEnRoute,
    marquerLivre,
    marquerEchec,
    supprimerColis,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}
